import { useForm } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogBody, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { OwnerPipeStatusBadge } from "@/components/projects/pipe-status-badge"
import { Plus, Trash2 } from "lucide-react"
import { useState } from "react"
import { router } from "@inertiajs/react"
import type { Owner } from "@/types"

type Props = {
  projectId: string
  owners: Owner[]
}

export function OwnersTab({ projectId, owners }: Props) {
  const [showForm, setShowForm] = useState(false)

  const { data, setData, post, processing, reset } = useForm({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    ownership_code: "",
    notes: "",
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/projects/${projectId}/owners`, {
      onSuccess: () => {
        reset()
        setShowForm(false)
      },
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Propriétaires</h3>
        <Button size="sm" onClick={() => setShowForm(!showForm)}>
          <Plus className="size-4" />
          Ajouter
        </Button>
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouveau propriétaire</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden">
            <DialogBody className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="owner-first-name">Prénom</Label>
                <Input
                  id="owner-first-name"
                  value={data.first_name}
                  onChange={(e) => setData("first_name", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="owner-last-name">Nom</Label>
                <Input
                  id="owner-last-name"
                  value={data.last_name}
                  onChange={(e) => setData("last_name", e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="owner-email">Email</Label>
                <Input
                  id="owner-email"
                  type="email"
                  value={data.email}
                  onChange={(e) => setData("email", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="owner-phone">Téléphone</Label>
                <Input
                  id="owner-phone"
                  value={data.phone_number}
                  onChange={(e) => setData("phone_number", e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="owner-code">Code propriété</Label>
              <Input
                id="owner-code"
                value={data.ownership_code}
                onChange={(e) => setData("ownership_code", e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="owner-notes">Notes</Label>
              <Textarea
                id="owner-notes"
                value={data.notes}
                onChange={(e) => setData("notes", e.target.value)}
                rows={2}
              />
            </div>
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={processing}>
                Enregistrer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {owners.length === 0 ? (
        <p className="text-muted-foreground text-sm">Aucun propriétaire</p>
      ) : (
        <div className="space-y-3">
          {owners.map((owner) => (
            <Card key={owner.id}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-sm">{owner.display_name}</CardTitle>
                    <OwnerPipeStatusBadge status={owner.pipe_status} className="text-xs" />
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    onClick={() => router.delete(`/projects/${projectId}/owners/${owner.id}`)}
                  >
                    <Trash2 className="size-3.5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid gap-2 text-sm md:grid-cols-3">
                  {owner.email && <p>Email : {owner.email}</p>}
                  {owner.phone_number && <p>Tél : {owner.phone_number}</p>}
                  {owner.ownership_code && <p>Code : {owner.ownership_code}</p>}
                </div>
                {owner.notes && <p className="text-muted-foreground text-sm mt-2">{owner.notes}</p>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
